// Launch an assessment from the portal (admin only): a two-step wizard.
// Step 1 creates the cycle (D1 first, Zoho mirror second — a CRM outage never
// blocks the launch); step 2 drives the EXISTING invitation APIs against the
// new cycle, pre-loading the church's leaders from the CRM. Abandoning step 2
// is safe: the cycle is real and resumable from its Invitations tab.

import { useEffect, useId, useMemo, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHealthFrame } from './HealthLayout';
import {
  healthApi,
  HealthApiError,
  type CreateCycleResponse,
  type FacilitatorFixupSkipped,
  type HealthSettings,
  type InstrumentVersionsResponse,
  type InviteSuggestion,
} from './api';
import { ROLE_ORDER, roleLabel } from './format';
import { RegistrarContact } from '../access/RegistrarContact';

type ContactCheck =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'error' }
  | {
      state: 'done';
      found: boolean;
      /** The email is on more than one CRM Contact (cca-5myzh). Sign-in would
       *  refuse whoever it is, and the server refuses the whole launch (409
       *  facilitator_ambiguous) before it writes anything, so the wizard says
       *  why and offers no fix-up. */
      ambiguous: boolean;
      name: string | null;
      isFacilitator: boolean;
    };

/** The field banner's sentence when the facilitator email is on more than one
 *  Contact, from the lookup or from a launch refused 409. It carries the fix
 *  and does not depend on who reads it: today the only health admin IS the
 *  Registrar, so "ask the Registrar" would send him to himself (Miranda S1). */
const AMBIGUOUS_FACILITATOR =
  "This email address is on more than one Contact in the CRM, so the portal can't tell which person is the facilitator. Leave it on exactly one Contact (merge the duplicates, or, on the other Contacts, move it to Secondary Email), then press Check again.";

/** The box beside the Launch row after a 409: only what happened to the press.
 *  The field banner above owns the explanation (Miranda S3). */
const LAUNCH_REFUSED =
  'Not launched — nothing was created or changed. The facilitator email needs attention.';

/** Step 2's line when a fix-up the admin ticked was not applied (Miranda
 *  S-A): the consequence first (Lazo L-1, Miranda P-1), then one sentence per
 *  cause naming which fix-up was dropped and where to act, then that the
 *  invitations are unaffected (they are added and sent by the admin, and only
 *  name the facilitator). A create that timed out may still have landed, so
 *  the create case says "if it's still missing": a second Contact on the
 *  mailbox would lock the facilitator out. `wasCreate` is what the launch
 *  asked for, recorded when it was sent (LeC a). */
const fixupSkippedLine = (cause: FacilitatorFixupSkipped, wasCreate: boolean): string => {
  const why =
    cause === 'lookup_failed'
      ? 'The CRM could not be checked just now'
      : 'The CRM could not be updated just now';
  const dropped = wasCreate
    ? `${why}, so their Contact was not created — if it's still missing, create it in the CRM with the Facilitator role.`
    : `${why}, so the Facilitator role was not added — add it on their Contact in the CRM.`;
  return `The facilitator can't sign in yet. ${dropped} The invitations below aren't affected.`;
};

const nzToday = (): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

const plusDays = (dateStr: string, days: number): string => {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  // eslint-disable-next-line cca/nz-time-day-boundary -- noon-UTC date maths, not a day boundary
  return d.toISOString().slice(0, 10);
};

export function NewCyclePage(): JSX.Element {
  const { role, reloadCycles } = useHealthFrame();
  const navigate = useNavigate();

  const [churches, setChurches] = useState<Array<{ id: string; name: string }> | null>(null);
  const [versions, setVersions] = useState<InstrumentVersionsResponse | null>(null);
  const [settings, setSettings] = useState<HealthSettings | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Step 1 form
  const today = useMemo(nzToday, []);
  const [churchId, setChurchId] = useState('');
  const [facilitatorName, setFacilitatorName] = useState('');
  const [facilitatorEmail, setFacilitatorEmail] = useState('');
  const [ensureFacilitator, setEnsureFacilitator] = useState(false);
  const [check, setCheck] = useState<ContactCheck>({ state: 'idle' });
  const [opensOn, setOpensOn] = useState(today);
  const [closesOn, setClosesOn] = useState(plusDays(today, 21));
  const [mode, setMode] = useState<'rostered' | 'open'>('rostered');
  const [familyId, setFamilyId] = useState('');
  const [version, setVersion] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // True when a launch is refused because the facilitator email is on more
  // than one Contact. The box beside the Launch row then says only what
  // happened (Miranda S3): the field banner above owns the explanation, and is
  // always on screen with it, because the 409 sets the field section's answer
  // and nothing older can overwrite that (checkSeq, below).
  const [launchRefusal, setLaunchRefusal] = useState(false);
  const refusalRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const ambiguousSentenceId = useId();
  // The newest answer wins (Miranda S-B, LeC S-b). A pointer press on Create
  // blurs the email field, and the blur starts a lookup alongside the launch;
  // if the launch's 409 lands first, that lookup must not overwrite it. Every
  // lookup takes a token, the 409 and every edit of the email bump the
  // sequence (Jorge F1), and a lookup applies its answer only while its token
  // is still current.
  const checkSeq = useRef(0);
  // The trimmed address the field's answer is about, answered or still in
  // flight; null once the email is edited. A blur on that same address asks
  // again for nothing (Miranda S-C): re-asking would put the field back to
  // "checking", hiding the fix-up offer and dropping the admin's tick from
  // the launch, under the pointer that is pressing Create.
  const askedEmail = useRef<string | null>(null);
  const stepTwoHeadingRef = useRef<HTMLHeadingElement>(null);
  const fixupWarnId = useId();

  // Step 2
  const [created, setCreated] = useState<CreateCycleResponse | null>(null);
  // Whether the launch asked to CREATE the facilitator's Contact (not grant
  // the role), fixed when it was sent: step 2 words a dropped fix-up from
  // this, not from a check that may have moved on since (LeC a).
  const [fixupSentAsCreate, setFixupSentAsCreate] = useState(false);
  const [suggestions, setSuggestions] = useState<InviteSuggestion[] | null>(null);
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [extraName, setExtraName] = useState('');
  const [extraEmail, setExtraEmail] = useState('');
  const [extras, setExtras] = useState<Array<{ name: string; email: string; role: string }>>([]);
  const [finishing, setFinishing] = useState<'idle' | 'sending' | 'saving'>('idle');
  const [inviteError, setInviteError] = useState<string | null>(null);

  const isAdmin = role === 'admin';

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([healthApi.adminChurches(), healthApi.instrumentVersions(), healthApi.settings()])
      .then(([c, v, s]) => {
        setChurches(c.churches);
        setVersions(v);
        setSettings(s.settings);
        setMode(s.settings.default_cycle_mode);
        setFamilyId(v.default_family_id);
        setVersion(v.default_version);
      })
      .catch(() => setLoadError(true));
  }, [isAdmin]);

  // A refused launch moves focus to the box that says so (Miranda S4): after
  // a keyboard press the focus was otherwise left on <body>. The move is what
  // announces it, so the box carries no alert role.
  useEffect(() => {
    if (launchRefusal) refusalRef.current?.focus();
  }, [launchRefusal]);

  // Arriving at step 2 moves focus to its heading (Miranda P-e): the Create
  // button that had it is gone, which would otherwise leave focus on <body>.
  useEffect(() => {
    if (created) stepTwoHeadingRef.current?.focus();
  }, [created]);

  if (!isAdmin) {
    return (
      <div className="card" style={{ maxWidth: 560, margin: '30px auto', textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.7 }}>
          Launching assessments is a Registrar task. If you believe you should have access, please
          contact the Registrar.
        </p>
        <RegistrarContact className="mt-1 text-ccaanz-grey" />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="card" style={{ maxWidth: 560, margin: '30px auto', textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.7 }}>
          The launch form could not be loaded. Please try again.
        </p>
      </div>
    );
  }
  if (!churches || !versions || !settings) {
    return <p style={{ color: 'var(--health-grey)' }}>Loading...</p>;
  }

  const family = versions.families.find((f) => f.id === familyId) ?? null;
  const emailOk = /.+@.+\..+/.test(facilitatorEmail.trim());
  const needsFixup =
    check.state === 'done' && !check.ambiguous && (!check.found || !check.isFacilitator) && emailOk;
  // The field banner is on screen: it owns the explanation, and the email
  // input is described by its sentence (Miranda P2, P-d).
  const ambiguousShown = check.state === 'done' && check.ambiguous;

  const runCheck = (): void => {
    if (!emailOk) return;
    // A fresh check supersedes a refused launch, as editing the email does:
    // the box beside the Launch row spoke of the answer before this one.
    setLaunchRefusal(false);
    checkSeq.current += 1;
    const token = checkSeq.current;
    // Trimmed, as every record of askedEmail is: checkOnBlur compares the
    // trimmed field, and a mismatch would re-ask on blur and drop a ticked
    // fix-up (Tyson r6). Pinned in NewCyclePage.dom.test.tsx by
    // "an address pasted with spaces round it is the address asked about".
    askedEmail.current = facilitatorEmail.trim();
    setCheck({ state: 'checking' });
    healthApi
      .contactLookup(facilitatorEmail.trim())
      .then((res) => {
        // A newer answer (a later lookup, or a launch refused 409) has
        // landed, or the email has been edited since this was asked.
        if (token !== checkSeq.current) return;
        if ('zoho_error' in res && res.zoho_error) {
          setCheck({ state: 'error' });
          return;
        }
        setCheck({
          state: 'done',
          found: res.found,
          ambiguous: res.ambiguous === true,
          name: res.name ?? null,
          isFacilitator: res.is_facilitator === true,
        });
        // The CRM's name fills an EMPTY field only: a name the admin typed
        // while the lookup was out is kept (Jorge F1).
        const crmName = res.found ? res.name : null;
        if (crmName) setFacilitatorName((typed) => typed || crmName);
      })
      .catch(() => {
        if (token === checkSeq.current) setCheck({ state: 'error' });
      });
  };

  /** Leaving the email field asks the CRM about it, unless the field's answer
   *  is already about this address (Miranda S-C, see askedEmail). */
  const checkOnBlur = (): void => {
    // Trimmed, as askedEmail is recorded: compare forms that differ and a
    // padded paste re-asks on blur, dropping a ticked fix-up (Tyson r6).
    // Pinned in NewCyclePage.dom.test.tsx by
    // "an address pasted with spaces round it is the address asked about".
    if (facilitatorEmail.trim() === askedEmail.current) return;
    runCheck();
  };

  const create = (force: boolean): void => {
    setCreating(true);
    setFormError(null);
    setLaunchRefusal(false);
    // What this launch asks of the fix-up, fixed now, as it is sent (LeC a).
    const sendsFixup = ensureFacilitator && needsFixup;
    const fixupIsCreate = sendsFixup && check.state === 'done' && !check.found;
    healthApi
      .createCycle({
        church_zoho_id: churchId,
        ...(facilitatorEmail.trim() ? { facilitator_email: facilitatorEmail.trim() } : {}),
        ...(facilitatorName.trim() ? { facilitator_name: facilitatorName.trim() } : {}),
        ...(sendsFixup ? { ensure_facilitator: true } : {}),
        ...(opensOn ? { opens_on: opensOn } : {}),
        ...(closesOn ? { closes_on: closesOn } : {}),
        mode,
        ...(version ? { instrument_version: version } : {}),
        ...(force ? { force: true } : {}),
      })
      .then((res) => {
        setFixupSentAsCreate(fixupIsCreate);
        setCreated(res);
        reloadCycles();
        healthApi
          .suggestions(res.cycle_id)
          .then((s) => {
            setSuggestions(s.suggestions);
            const initialRoles: Record<string, string> = {};
            for (const sug of s.suggestions) initialRoles[sug.zoho_contact_id] = sug.suggested_role;
            setRoles(initialRoles);
          })
          .catch(() => setSuggestions([]));
      })
      .catch((err: unknown) => {
        const code = err instanceof HealthApiError ? err.code : null;
        if (code === 'church_has_open_cycle') {
          if (
            window.confirm(
              'This church already has an open assessment. Launch another one alongside it?',
            )
          ) {
            create(true);
            return;
          }
          setFormError(null);
        } else if (code === 'version_not_found') {
          setFormError('That instrument version could not be found. Choose another.');
        } else if (code === 'closes_before_opens') {
          setFormError('The closing date is before the opening date.');
        } else if (code === 'invalid_date') {
          setFormError('Please give the dates as calendar dates.');
        } else if (code === 'zoho_unavailable' || code === 'church_not_found') {
          setFormError('The church could not be confirmed in the CRM. Please try again.');
        } else if (code === 'module_disabled') {
          setFormError('The health module is switched off in Settings, so nothing can launch.');
        } else if (code === 'facilitator_ambiguous') {
          // Refused before any write. The field section takes the ambiguous
          // answer (Miranda S2): its banner explains, and no fix-up is offered
          // or sent. The box beside the button says only what happened. This
          // answer is the newest, so bumping the sequence retires any lookup
          // still in flight from before it (Miranda S-B). It is an answer
          // about the address it sent, so a blur on that address asks again
          // for nothing (Miranda S-C).
          checkSeq.current += 1;
          askedEmail.current = facilitatorEmail.trim();
          setCheck({
            state: 'done',
            found: false,
            ambiguous: true,
            name: null,
            isFacilitator: false,
          });
          setEnsureFacilitator(false);
          setLaunchRefusal(true);
        } else {
          setFormError('The assessment could not be created. Please try again.');
        }
      })
      .finally(() => setCreating(false));
  };

  /** The refusal box's one action: back to the field that needs attention. */
  const changeFacilitatorEmail = (): void => {
    const input = emailRef.current;
    if (!input) return;
    input.scrollIntoView({ block: 'center' });
    input.focus({ preventScroll: true });
  };

  const finish = (send: boolean): void => {
    if (!created) return;
    const invitees = [
      ...(suggestions ?? [])
        .filter((s) => ticked[s.zoho_contact_id] && s.email)
        .map((s) => ({
          name: s.name,
          email: s.email as string,
          role: roles[s.zoho_contact_id] ?? s.suggested_role,
        })),
      ...extras,
    ];
    setFinishing(send ? 'sending' : 'saving');
    setInviteError(null);
    const add =
      invitees.length > 0
        ? healthApi.addInvitees(created.cycle_id, invitees)
        : Promise.resolve(null);
    add
      .then(() =>
        send && invitees.length > 0 ? healthApi.sendPendingInvites(created.cycle_id) : null,
      )
      .then(() => navigate(`/portal/health/${created.cycle_id}`))
      .catch(() => {
        setInviteError(
          'The invitations could not be saved. The assessment itself exists — you can finish the list on its Invitations tab.',
        );
        setFinishing('idle');
      });
  };

  // ── Step 2 ─────────────────────────────────────────────────────────────────
  if (created) {
    return (
      <div
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16, maxWidth: 760 }}
      >
        <div>
          {/* Focused on arrival (P-e), so it carries the module's focus ring
              (P-3), and is described by the warn line when there is one
              (P-4), so a dropped fix-up is read out with it. */}
          <h1
            ref={stepTwoHeadingRef}
            tabIndex={-1}
            className="health-step-heading"
            aria-describedby={created.facilitator_fixup_skipped ? fixupWarnId : undefined}
            style={{ fontSize: 22, marginBottom: 4 }}
          >
            Invite the leaders
          </h1>
          <p style={{ fontSize: 13.5, color: 'var(--health-grey)', lineHeight: 1.7 }}>
            The assessment is created. Tick who should be invited — the list below comes from the
            church's CRM personnel — add anyone missing, and send. You can also do all of this later
            from the assessment's Invitations tab.
          </p>
        </div>

        {!created.zoho_synced && (
          <div className="health-banner" role="status">
            The assessment was created in the portal, but its CRM record could not be written just
            now. Everything here works regardless; the CRM copy can be linked later.
          </div>
        )}
        {created.facilitator_fixed && (
          <div className="health-banner health-banner-blue" role="status">
            {created.facilitator_fixed === 'created'
              ? 'A Contact was created in the CRM for the facilitator, with the Facilitator role — they can sign in straight away.'
              : 'The Facilitator role was added to their existing Contact in the CRM — they can sign in straight away.'}
          </div>
        )}
        {created.facilitator_fixup_skipped && (
          <div id={fixupWarnId} className="health-banner health-banner-warn" role="status">
            {fixupSkippedLine(created.facilitator_fixup_skipped, fixupSentAsCreate)}
          </div>
        )}

        <div className="card">
          <h2 className="health-section-heading">
            <span>Suggested from the CRM</span>
          </h2>
          {suggestions === null && (
            <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>Loading suggestions...</p>
          )}
          {suggestions !== null && suggestions.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>
              No personnel with email addresses are recorded for this church in the CRM. Add people
              below.
            </p>
          )}
          {(suggestions ?? []).map((s) => (
            <div
              key={s.zoho_contact_id}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                flexWrap: 'wrap',
                padding: '7px 0',
                borderBottom: '1px solid var(--health-border-soft)',
              }}
            >
              <label
                style={{
                  display: 'inline-flex',
                  gap: 8,
                  alignItems: 'center',
                  flex: '1 1 240px',
                  minWidth: 0,
                }}
              >
                <input
                  type="checkbox"
                  disabled={!s.email}
                  checked={Boolean(ticked[s.zoho_contact_id])}
                  onChange={() =>
                    setTicked((t) => ({ ...t, [s.zoho_contact_id]: !t[s.zoho_contact_id] }))
                  }
                />
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{s.name}</span>
                <span
                  style={{
                    fontSize: 12.5,
                    color: 'var(--health-grey)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.email ?? 'no email on file'}
                </span>
              </label>
              <select
                className="health-input"
                style={{ width: 'auto' }}
                value={roles[s.zoho_contact_id] ?? s.suggested_role}
                aria-label={`Role for ${s.name}`}
                onChange={(e) => setRoles((r) => ({ ...r, [s.zoho_contact_id]: e.target.value }))}
              >
                {ROLE_ORDER.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <div
            style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap', marginTop: 12 }}
          >
            <div className="health-field" style={{ flex: '1 1 160px' }}>
              <span className="health-input-label">Add someone else</span>
              <input
                className="health-input"
                placeholder="Name"
                aria-label="Additional invitee name"
                value={extraName}
                onChange={(e) => setExtraName(e.target.value)}
              />
            </div>
            <div className="health-field" style={{ flex: '1 1 200px' }}>
              <span className="health-input-label">Email</span>
              <input
                className="health-input"
                placeholder="them@example.org"
                aria-label="Additional invitee email"
                value={extraEmail}
                onChange={(e) => setExtraEmail(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={!/.+@.+\..+/.test(extraEmail.trim())}
              onClick={() => {
                setExtras((x) => [
                  ...x,
                  { name: extraName.trim(), email: extraEmail.trim(), role: 'other' },
                ]);
                setExtraName('');
                setExtraEmail('');
              }}
            >
              Add to the list
            </button>
          </div>
          {extras.length > 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginTop: 8 }}>
              Also inviting: {extras.map((x) => x.name || x.email).join(', ')}
            </p>
          )}
        </div>

        {inviteError && (
          <div className="health-banner health-banner-red" role="alert">
            {inviteError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              finishing !== 'idle' ||
              ((suggestions ?? []).every((s) => !ticked[s.zoho_contact_id]) && extras.length === 0)
            }
            onClick={() => finish(true)}
          >
            {finishing === 'sending' ? 'Sending...' : 'Add and send invitations'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={finishing !== 'idle'}
            onClick={() => finish(false)}
          >
            {finishing === 'saving' ? 'Saving...' : 'Save for later'}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 1 ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16, maxWidth: 720 }}>
      <div>
        <h1 style={{ fontSize: 22, marginBottom: 4 }}>New assessment</h1>
        <p style={{ fontSize: 13.5, color: 'var(--health-grey)', lineHeight: 1.7 }}>
          Launch a Church Health Assessment for a church. The CRM record is written for you; next
          you will choose who to invite.
        </p>
      </div>

      {formError && (
        <div className="health-banner health-banner-red" role="alert">
          {formError}
        </div>
      )}

      <div className="card">
        <h2 className="health-section-heading">
          <span>Church and facilitator</span>
        </h2>
        <div className="health-field-grid">
          <div className="health-field health-field-wide">
            <span className="health-input-label">Church</span>
            <select
              className="health-input"
              value={churchId}
              aria-label="Church"
              onChange={(e) => setChurchId(e.target.value)}
            >
              <option value="">Choose a church...</option>
              {churches.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  {ch.name}
                </option>
              ))}
            </select>
          </div>
          <div className="health-field">
            <span className="health-input-label">Facilitator name</span>
            <input
              className="health-input"
              value={facilitatorName}
              aria-label="Facilitator name"
              placeholder="Optional"
              onChange={(e) => setFacilitatorName(e.target.value)}
            />
          </div>
          <div className="health-field">
            <span className="health-input-label">Facilitator email</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                ref={emailRef}
                className="health-input"
                value={facilitatorEmail}
                aria-label="Facilitator email"
                aria-invalid={ambiguousShown ? true : undefined}
                aria-describedby={ambiguousShown ? ambiguousSentenceId : undefined}
                placeholder="Optional"
                onChange={(e) => {
                  setFacilitatorEmail(e.target.value);
                  // An edit retires every answer about the address before it
                  // (Jorge F1): a lookup still out can no longer land, or
                  // fill the name, on this one, and the next blur asks again.
                  checkSeq.current += 1;
                  askedEmail.current = null;
                  setCheck({ state: 'idle' });
                  setEnsureFacilitator(false);
                  setLaunchRefusal(false);
                }}
                onBlur={checkOnBlur}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={!emailOk || check.state === 'checking'}
                onClick={runCheck}
              >
                {check.state === 'checking' ? '...' : 'Check'}
              </button>
            </div>
            {check.state === 'done' && check.found && check.isFacilitator && (
              <span className="health-field-hint">
                {check.name ?? 'This Contact'} is in the CRM with the Facilitator role — they can
                sign in.
              </span>
            )}
            {check.state === 'error' && (
              <span className="health-field-hint">
                The CRM could not be checked just now; the assessment can still be created.
              </span>
            )}
          </div>
        </div>
        {ambiguousShown && (
          <div className="health-banner health-banner-red" role="alert" style={{ marginTop: 10 }}>
            {/* The email input is described by this sentence alone, not the
                mailto line below it (Miranda P-d). */}
            <p id={ambiguousSentenceId}>{AMBIGUOUS_FACILITATOR}</p>
            <RegistrarContact className="mt-1" />
          </div>
        )}
        {needsFixup && (
          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'baseline',
              fontSize: 13,
              marginTop: 10,
              lineHeight: 1.6,
            }}
          >
            <input
              type="checkbox"
              checked={ensureFacilitator}
              onChange={(e) => setEnsureFacilitator(e.target.checked)}
            />
            <span>
              {check.state === 'done' && !check.found
                ? 'No Contact in the CRM has this email, so they could not sign in to facilitate. Create the Contact with the Facilitator role now.'
                : `${check.state === 'done' ? (check.name ?? 'This Contact') : 'This Contact'} is in the CRM but does not hold the Facilitator role, so they could not sign in. Add the role now.`}
            </span>
          </label>
        )}
      </div>

      <div className="card">
        <h2 className="health-section-heading">
          <span>Window and shape</span>
        </h2>
        <div className="health-field-grid">
          <div className="health-field">
            <span className="health-input-label">Opens</span>
            <input
              className="health-input"
              type="date"
              value={opensOn}
              aria-label="Opening date"
              onChange={(e) => setOpensOn(e.target.value)}
            />
            <span className="health-field-hint">
              Survey links only work from this day (NZ time).
            </span>
          </div>
          <div className="health-field">
            <span className="health-input-label">Closes</span>
            <input
              className="health-input"
              type="date"
              value={closesOn}
              aria-label="Closing date"
              onChange={(e) => setClosesOn(e.target.value)}
            />
            <span className="health-field-hint">Stays open through this whole day (NZ time).</span>
          </div>
          <div className="health-field">
            <span className="health-input-label">Mode</span>
            <select
              className="health-input"
              value={mode}
              aria-label="Cycle mode"
              onChange={(e) => setMode(e.target.value === 'open' ? 'open' : 'rostered')}
            >
              <option value="rostered">Rostered (named invite list, reminders)</option>
              <option value="open">Open link (anyone with the shared link)</option>
            </select>
          </div>
          <div className="health-field">
            <span className="health-input-label">Instrument</span>
            <div style={{ display: 'grid', gap: 6 }}>
              <select
                className="health-input"
                value={familyId}
                aria-label="Instrument"
                onChange={(e) => {
                  const famId = e.target.value;
                  setFamilyId(famId);
                  const fam = versions.families.find((f) => f.id === famId);
                  setVersion(
                    fam?.versions.find((v) => v.is_default)?.version ??
                      fam?.versions[fam.versions.length - 1]?.version ??
                      '',
                  );
                }}
              >
                {versions.families.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
              <select
                className="health-input"
                value={version}
                aria-label="Instrument version"
                onChange={(e) => setVersion(e.target.value)}
              >
                {(family?.versions ?? []).map((v) => (
                  <option key={v.version} value={v.version}>
                    {v.version}
                    {v.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {launchRefusal && (
          <div
            ref={refusalRef}
            tabIndex={-1}
            className="health-banner health-banner-red health-launch-refusal"
            style={{ marginTop: 18 }}
          >
            <p>{LAUNCH_REFUSED}</p>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ marginTop: 8 }}
              onClick={changeFacilitatorEmail}
            >
              Change the facilitator email
            </button>
          </div>
        )}
        <div className="health-form-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/portal/health')}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!churchId || creating}
            onClick={() => create(false)}
          >
            {creating ? 'Creating...' : 'Create and choose invitees'}
          </button>
        </div>
      </div>
    </div>
  );
}
